import React, { useMemo, useState } from "react";

import { formatNumber, useTranslation } from "utils";

import { CROP_TYPE_FCPO, FieldListField, FieldPracticeResponse } from "models";

import { Button, Drawer, Flex, Icon, Link, Option, Select, Typography } from "components";

import {
  CROP_TYPES_LOCKED_AFTER_VERIFICATION,
  CROP_TYPES_REQUIRING_MAIN_CROP,
  CROP_TYPE_OPTIONS,
  CROP_TYPE_TRANSLATION_KEY,
  allFieldsHaveAnyCropType,
  getFieldsMissingMainCrop,
  getFieldsWithLockedYear,
  getFieldsWithoutCropType,
  sumFieldHectares,
} from "pages/app/all-field-view/utils/CropTypeUtils";
import { FieldMap } from "pages/app/historical-practice-data-table-view/components/FieldMap";
import { PracticeTableTooltip } from "pages/app/historical-practice-data-table-view/components/PracticeTableTooltip";

export type AddCropTypeDrawerProps = {
  fields: FieldListField[];
  fieldPractices: Record<string, FieldPracticeResponse>;
  year: number;
  availableYears: number[];
  // the selection the drawer was opened from already pins the harvest year, so it's shown but not changeable
  isYearLocked?: boolean;
  visible: boolean;
  onClose: () => void;
  // only the fields eligible for the chosen crop type/year are handed back — the caller decides what to build
  // from them (a single crop for one field, a bulk batch for several)
  onContinue: (year: number, cropType: CROP_TYPE_FCPO, eligibleFields: FieldListField[]) => void;
};

export const AddCropTypeDrawer = ({
  fields,
  fieldPractices,
  year,
  availableYears,
  isYearLocked = false,
  visible,
  onClose,
  onContinue,
}: AddCropTypeDrawerProps) => {
  const { t } = useTranslation(["allFieldsDataTableView", "fieldTimeline", "farm"]);

  const [selectedYear, setSelectedYear] = useState(year);
  const [selectedCropType, setSelectedCropType] = useState<CROP_TYPE_FCPO | undefined>();
  const [showDetails, setShowDetails] = useState(false);

  const isMultiFieldSelection = fields.length > 1;
  const totalHectares = sumFieldHectares(fields);

  const eligibleFieldsByCropType = useMemo(
    () =>
      CROP_TYPE_OPTIONS.reduce<Record<string, FieldListField[]>>(
        (acc, cropType) => ({ ...acc, [cropType]: getFieldsWithoutCropType({ fields, fieldPractices, year: selectedYear, cropType }) }),
        {}
      ),
    [fields, fieldPractices, selectedYear]
  );

  const fieldsMissingMainCrop = useMemo(() => getFieldsMissingMainCrop({ fields, fieldPractices, year: selectedYear }), [fields, fieldPractices, selectedYear]);
  // Main crop becomes required only once every field already has a Cover or Undersowing crop but no Main crop yet
  const everyFieldHasCoverOrUndersowingCrop = useMemo(
    () => allFieldsHaveAnyCropType(fields, fieldPractices, selectedYear, [CROP_TYPE_FCPO.COVER, CROP_TYPE_FCPO.UNDERSOWING]),
    [fields, fieldPractices, selectedYear]
  );
  const isMainCropRequired = fieldsMissingMainCrop.length === fields.length && everyFieldHasCoverOrUndersowingCrop;

  const fieldsWithLockedYearByCropType = useMemo(
    () =>
      CROP_TYPES_LOCKED_AFTER_VERIFICATION.reduce<Record<string, FieldListField[]>>(
        (acc, cropType) => ({ ...acc, [cropType]: getFieldsWithLockedYear({ fields, fieldPractices, year: selectedYear, cropType }) }),
        {}
      ),
    [fields, fieldPractices, selectedYear]
  );

  const getCropTypeHint = (cropType: CROP_TYPE_FCPO) => {
    const cropTypeKey = CROP_TYPE_TRANSLATION_KEY[cropType];
    const cropTypeName = t(`fieldTimeline:addCrops.cropTypeName.${cropTypeKey}`);
    const eligibleCount = eligibleFieldsByCropType[cropType].length;
    const missingMainCount = CROP_TYPES_REQUIRING_MAIN_CROP.includes(cropType) ? fieldsMissingMainCrop.length : 0;
    const lockedYearCount = fieldsWithLockedYearByCropType[cropType]?.length ?? 0;

    if (eligibleCount === 0) {
      if (missingMainCount === fields.length) {
        return isMultiFieldSelection
          ? t("fieldTimeline:addCrops.allFieldsMissingMainCrop", { count: fields.length, cropType: cropTypeName, year: selectedYear })
          : t("fieldTimeline:addCrops.fieldMissingMainCrop", { cropType: cropTypeName, year: selectedYear });
      }
      if (lockedYearCount === fields.length) {
        return isMultiFieldSelection
          ? t("fieldTimeline:addCrops.allFieldsYearLocked", { count: fields.length, cropType: cropTypeName, year: selectedYear })
          : t("fieldTimeline:addCrops.fieldYearLocked", { cropType: cropTypeName, year: selectedYear });
      }
      return isMultiFieldSelection
        ? t("fieldTimeline:addCrops.allFieldsTaken", { count: fields.length, cropType: cropTypeName, year: selectedYear })
        : t("fieldTimeline:addCrops.fieldTaken", { cropType: cropTypeName, year: selectedYear });
    }

    const skippedCount = fields.length - eligibleCount;
    if (skippedCount > 0) {
      return t("fieldTimeline:addCrops.someFieldsSkipped", { skipped: skippedCount, total: fields.length, year: selectedYear, context: cropTypeKey });
    }
    if (cropType === CROP_TYPE_FCPO.MAIN && isMainCropRequired) {
      return isMultiFieldSelection
        ? t("fieldTimeline:addCrops.allFieldsMainCropRequired", { count: fields.length, year: selectedYear })
        : t("fieldTimeline:addCrops.fieldMainCropRequired");
    }
    return t(`fieldTimeline:addCrops.cropTypeDescription.${cropTypeKey}`);
  };

  const eligibleFields = selectedCropType ? eligibleFieldsByCropType[selectedCropType] : [];
  const canContinue = eligibleFields.length > 0;

  const handleClose = () => {
    setSelectedYear(year);
    setSelectedCropType(undefined);
    setShowDetails(false);
    onClose();
  };

  return (
    <Drawer
      className="add-crop-type-drawer"
      dataTestId="add-crop-type-drawer"
      title={t(isMultiFieldSelection ? "fieldTimeline:addCrops.titleMultiple" : "fieldTimeline:addCrops.titleSingle")}
      visible={visible}
      onClose={handleClose}
      footer={
        <Button
          block
          type="primary"
          disabled={!canContinue}
          dataTestId="add-crop-type-drawer-continue"
          onClick={() => selectedCropType && onContinue(selectedYear, selectedCropType, eligibleFields)}
        >
          {canContinue
            ? t("fieldTimeline:addCrops.continue", { count: eligibleFields.length })
            : t("fieldTimeline:addCrops.addToFields", { count: fields.length })}
        </Button>
      }
    >
      {isMultiFieldSelection ? (
        <>
          <Flex align="center" justify="space-between">
            <Flex align="center" gap="0.75rem" className="add-crop-type-drawer-summary">
              <Flex align="center" justify="center" className="add-crop-type-drawer-summary-icon">
                <Icon iconName="tabler/copy" size="l" />
              </Flex>
              <Flex vertical>
                <Typography variant="paragraph-m" className="add-crop-type-drawer-summary-title">
                  {t("fieldTimeline:addCrops.fieldsSelected", { count: fields.length })}
                </Typography>
                <Typography variant="paragraph-s" className="add-crop-type-drawer-summary-size">
                  {`${totalHectares.toFixed(2)} ${t("allFieldView:fieldContextMenu.ha")}`}
                </Typography>
              </Flex>
            </Flex>
            <Link dataTestId="add-crop-type-drawer-toggle-details" type="primary-link" onClick={() => setShowDetails((prev) => !prev)}>
              <Flex align="center" gap="0.25rem">
                {t(showDetails ? "fieldTimeline:addCrops.hideDetails" : "fieldTimeline:addCrops.showDetails")}
                <Icon iconName={showDetails ? "tabler/chevron-up" : "tabler/chevron-down"} size="xs" />
              </Flex>
            </Link>
          </Flex>
          {showDetails && (
            <Flex vertical gap="0.5rem" className="add-crop-type-drawer-field-list">
              {fields.map((field) => (
                <Flex key={field.id} align="center" justify="space-between" className="add-crop-type-drawer-field-list-item">
                  <Flex align="center" gap="0.75rem">
                    <div className="add-crop-type-drawer-field-list-thumbnail">
                      <FieldMap field={field} />
                    </div>
                    <Typography variant="paragraph-m" className="margin-0">
                      {field.name}
                    </Typography>
                  </Flex>
                  <Typography variant="paragraph-s" color="grey-600" className="margin-0">
                    {`${formatNumber({ number: field.size })} ha`}
                  </Typography>
                </Flex>
              ))}
            </Flex>
          )}
        </>
      ) : (
        <Flex className="add-crop-type-drawer-field-header" align="center">
          <FieldMap field={fields[0]} />
          <Flex vertical>
            <PracticeTableTooltip title={fields[0]?.name || t("farm:unknownFieldName")}>
              {({ ref }) => (
                <div ref={ref} className={`margin-0 historical-practice-data-table-view-field-name ${fields[0]?.name ? "" : "red-text"}`}>
                  {fields[0]?.name || t("farm:unknownFieldName")}
                </div>
              )}
            </PracticeTableTooltip>
            <Typography variant="paragraph-s" color="grey-600" className="margin-0">
              {formatNumber({ number: fields[0]?.size })} ha
            </Typography>
          </Flex>
        </Flex>
      )}

      <Flex vertical gap="0.75rem" className="add-crop-type-drawer-section">
        <Typography variant="paragraph-m" bold>
          {t("fieldTimeline:addCrops.harvestYear")}
        </Typography>
        <Select
          label={t("fieldTimeline:addCrops.year")}
          value={selectedYear}
          disabled={isYearLocked}
          dataTestId="add-crop-type-drawer-year"
          onChange={(newYear: number) => {
            setSelectedYear(newYear);
            if (selectedCropType && !getFieldsWithoutCropType({ fields, fieldPractices, year: newYear, cropType: selectedCropType }).length) {
              setSelectedCropType(undefined);
            }
          }}
        >
          {availableYears.map((availableYear) => (
            <Option key={availableYear} value={availableYear}>
              {availableYear}
            </Option>
          ))}
        </Select>
      </Flex>

      <Flex vertical gap="0.5rem" className="add-crop-type-drawer-section">
        <Typography variant="paragraph-m" bold>
          {t("fieldTimeline:addCrops.cropType")}
        </Typography>
        {CROP_TYPE_OPTIONS.map((cropType) => {
          const isDisabled = eligibleFieldsByCropType[cropType].length === 0;
          const isSelected = selectedCropType === cropType;
          // main crop is mandatory — flag it as required rather than merely "selected" while every field in this
          // selection is still missing one
          const isRequiredAndMissing = cropType === CROP_TYPE_FCPO.MAIN && isMainCropRequired && !isSelected;

          return (
            <button
              key={cropType}
              type="button"
              disabled={isDisabled}
              data-testid={`add-crop-type-drawer-crop-type-${cropType.toLowerCase()}`}
              className={`add-crop-type-card ${isSelected ? "selected" : ""} ${isDisabled ? "locked" : ""} ${isRequiredAndMissing ? "required" : ""}`}
              onClick={() => setSelectedCropType(cropType)}
            >
              <Flex vertical className="add-crop-type-card-content">
                <Flex align="center" gap="0.25rem">
                  <Typography variant="paragraph-m" bold className="margin-0" color={isDisabled ? "grey-600" : undefined}>
                    {t(`fieldTimeline:addCrops.cropTypeName.${CROP_TYPE_TRANSLATION_KEY[cropType]}`)}
                  </Typography>
                  {isDisabled && <Icon iconName="tabler/lock" size="s" color="grey" />}
                </Flex>
                <Typography variant="paragraph-s" color="grey-600" className="margin-0">
                  {getCropTypeHint(cropType)}
                </Typography>
              </Flex>
            </button>
          );
        })}
      </Flex>
    </Drawer>
  );
};
